import { NextStepBanner } from '@/ui/NextStepBanner';
import { type PassoDeProgresso } from '@/ui/ProgressTimeline';

export interface TimelineNextStepBannerProps {
  /** Passos usados na `<ProgressTimeline />` daquela tela. */
  passos: PassoDeProgresso[];
  /** Texto do botão de ação primária (opcional). */
  actionLabel?: string;
  /** Callback do botão (opcional). Sem ele, o banner fica só informativo. */
  onAction?: () => void;
  /** Override do título — padrão: "Próximo passo: {label do passo atual}". */
  title?: string;
  sticky?: boolean;
  variant?: 'info' | 'success';
  className?: string;
}

/**
 * Wrapper padrão que consome `passo.proximaAcao` do passo `current` de uma
 * timeline e renderiza o `<NextStepBanner />`. Cópia estrutural do protótipo
 * (`TimelineNextStepBanner.tsx`) — garante consistência de copy/visual em
 * todas as telas de detalhe que usam `<ProgressTimeline />`.
 *
 * Não renderiza nada se não houver passo `current` ou `proximaAcao` ausente.
 */
export function TimelineNextStepBanner({
  passos,
  actionLabel,
  onAction,
  title,
  sticky,
  variant,
  className,
}: TimelineNextStepBannerProps) {
  const alvo =
    passos.find((p) => p.status === 'current' && p.proximaAcao) ??
    passos.find((p) => p.proximaAcao && p.status !== 'completed' && p.status !== 'skipped');

  if (!alvo?.proximaAcao) {
    return null;
  }

  return (
    <NextStepBanner
      title={title ?? `Próximo passo: ${alvo.label}`}
      description={alvo.proximaAcao}
      actionLabel={actionLabel}
      onAction={onAction}
      sticky={sticky}
      variant={variant}
      className={className}
    />
  );
}

export default TimelineNextStepBanner;
